/**
 * SpaceGame.jsx
 *
 * Canvas asteroid-dodge game — game logic/physics/audio/recording are
 * UNCHANGED from the original build. Only the presentation layer was
 * redone in a "mech cockpit" style for the modern-gaming redesign:
 *   - The old measured-PNG hex bezel is replaced with a CSS-drawn
 *     cockpit display (brushed-steel bezel, rivets, glowing cyan rim)
 *     so the frame is fully fluid at any aspect ratio — no per-pixel
 *     measurements tied to an external image.
 *   - A 3-lives system with a brief invincibility/blink window after a hit.
 *   - A proper DOM HUD (score / lives / best / mute) rendered ABOVE the
 *     cockpit as a row of HUD chips.
 *   - On-screen touch controls appear on mobile/narrow viewports, styled
 *     as circular robotic thumb-buttons mounted on the bezel corners.
 *   - Multi-layer parallax stars, a gradient-shaded ship with a flickering
 *     engine trail, gradient/cratered asteroids, and particle bursts on
 *     hits/death/win, all recoloured to the cyan/violet HUD palette.
 *   - Keyboard, mouse (hover-follow), touch-drag, and the on-screen
 *     buttons all drive the same ship-position logic.
 *
 * Status machine: idle → countdown → playing → lost | won
 *   'countdown' is a ~1.6s "3-2-1-GO" beat inserted between pressing
 *   LAUNCH/RETRY and gameplay actually starting — ship engines flicker to
 *   life, on-screen controls fade in early, and each number pops with its
 *   own beep. See start()/COUNTDOWN_STEPS. All the "juice" (screen shake +
 *   flash on hits, life-icon shatter, score pop, progress-bar shimmer,
 *   button glow, overlay pop-in) lives beside the state it reacts to below
 *   and in the matching classes in index.css; prefers-reduced-motion turns
 *   all of it off without touching game logic.
 *
 * Props:
 *   onWin {(blob: Blob|null, score: number) => void}  Called when the
 *     player reaches TARGET_SCORE, with the recorded clip (if any) and
 *     the final score so it can travel with the wallet submission.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import useIsMobile from '../hooks/useIsMobile';
import GlowText from './GlowText';
import RoboticButton from './RoboticButton';

const TARGET_SCORE = 100;
const MAX_LIVES = 3;
const INVINCIBLE_MS = 1300;

// Steps of the pre-launch countdown — freq drives the beep pitch, so it
// climbs step to step and resolves on a bright "GO" flourish.
const COUNTDOWN_STEPS = [
  { val: 3,    freq: 392 },
  { val: 2,    freq: 440 },
  { val: 1,    freq: 523 },
  { val: 'GO', freq: 784 },
];
const COUNTDOWN_STEP_MS = 420;
const COUNTDOWN_GO_HOLD_MS = 320;

// ── Cockpit frame geometry ────────────────────────────────────────────
// CSS-drawn bezel now (no external artwork), so the screen simply fills
// the bezel's own padding box — fully fluid at any size/aspect ratio.
const FRAME_ASPECT = '4 / 3';

const safeStorage = {
  get: (key) => { try { return localStorage.getItem(key); } catch { return null; } },
  set: (key, val) => { try { localStorage.setItem(key, val); } catch {} },
};

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

// Spawns a burst of fading square particles at (x, y) into the given array
function spawnParticles(arr, x, y, count, { speed = 0.18, life = 550, colors = ['#00e5ff'], size = 3 } = {}) {
  for (let i = 0; i < count; i++) {
    const ang = Math.random() * Math.PI * 2;
    const spd = speed * (0.5 + Math.random());
    arr.push({
      x, y,
      vx: Math.cos(ang) * spd,
      vy: Math.sin(ang) * spd,
      life, maxLife: life,
      size: size * (0.6 + Math.random() * 0.9),
      color: colors[Math.floor(Math.random() * colors.length)],
    });
  }
}

// ── Small presentational helpers ──────────────────────────────────────
function LifeIcon({ filled }) {
  const [justLost, setJustLost] = useState(false);
  const wasFilled = useRef(filled);

  useEffect(() => {
    const justLostLife = wasFilled.current && !filled;
    wasFilled.current = filled;
    if (justLostLife) {
      setJustLost(true);
      const t = setTimeout(() => setJustLost(false), 450);
      return () => clearTimeout(t);
    }
  }, [filled]);

  return (
    <svg
      width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"
      className={justLost ? 'life-lost-anim' : undefined}
      style={{ overflow: 'visible' }}
    >
      <polygon
        points="8,0.5 14.5,4.2 14.5,11.8 8,15.5 1.5,11.8 1.5,4.2"
        fill={filled ? 'var(--cyan)' : 'transparent'}
        stroke={filled ? 'var(--cyan)' : 'var(--steel)'}
        strokeWidth="1.5"
        style={filled ? { filter: 'drop-shadow(0 0 4px var(--cyan))' } : undefined}
      />
    </svg>
  );
}

function TouchButton({ side, onDown, onUp }) {
  const isLeft = side === 'left';
  return (
    <button
      type="button"
      aria-label={isLeft ? 'Move left' : 'Move right'}
      onPointerDown={(e) => { e.preventDefault(); onDown(); }}
      onPointerUp={(e) => { e.preventDefault(); onUp(); }}
      onPointerLeave={onUp}
      onPointerCancel={onUp}
      onContextMenu={(e) => e.preventDefault()}
      className="no-select active:scale-90"
      style={{
        position: 'absolute',
        [isLeft ? 'left' : 'right']: '4%',
        bottom: '6%',
        width: 'clamp(46px, 15vw, 64px)',
        height: 'clamp(46px, 15vw, 64px)',
        borderRadius: '50%',
        background: 'radial-gradient(circle at 35% 30%, #1c2947, #070a13 75%)',
        border: '2px solid var(--cyan)',
        boxShadow: '0 0 18px rgba(0,229,255,0.55), inset 0 0 10px rgba(0,229,255,0.15)',
        color: 'var(--cyan)',
        fontSize: 'clamp(16px, 5vw, 24px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        touchAction: 'none',
        cursor: 'pointer',
        zIndex: 15,
        transition: 'transform 0.1s',
      }}
    >
      {isLeft ? '◀' : '▶'}
    </button>
  );
}

export default function SpaceGame({ onWin }) {
  const canvasRef   = useRef(null);
  const wrapRef     = useRef(null);
  const audioRef    = useRef({ ctx: null, master: null });
  const recorderRef = useRef(null);
  const chunksRef   = useRef([]);
  const isMobile    = useIsMobile();

  const [status,  setStatus]  = useState('idle');
  const [score,   setScore]   = useState(0);
  const [lives,   setLives]   = useState(MAX_LIVES);
  const [muted,   setMuted]   = useState(false);
  const [shaking, setShaking] = useState(false);
  const [countdownVal, setCountdownVal] = useState(null); // 3 | 2 | 1 | 'GO' | null
  const countdownTimers = useRef([]);
  const [best,    setBest]    = useState(
    () => Number(safeStorage.get('hexabots_best') || 0)
  );

  // ── All mutable game state in one ref — avoids per-frame re-renders ──
  const S = useRef({
    w: 800, h: 480,
    scale:      1,
    ship:       { x: 400, y: 420, w: 34, h: 26 },
    keys:       { left: false, right: false },
    asteroids:  [],
    particles:  [],
    starsFar:   [],
    starsNear:  [],
    score:      0,
    lives:      MAX_LIVES,
    lastSpawn:  0,
    spawnEvery: 460,
    invincibleUntil: 0,
    last:       0,
    raf:        0,
    pointerX:   null,
    status:     'idle',
  });

  // ── Recording ─────────────────────────────────────────────────────
  const startRecording = useCallback(() => {
    const canvas = canvasRef.current;
    // Some mobile browsers implement MediaRecorder but not
    // canvas.captureStream (or vice versa) — checking both up front,
    // instead of just MediaRecorder, avoids a hard crash on those
    // devices and lets us fall back to "recording unavailable" cleanly.
    if (!canvas || typeof MediaRecorder === 'undefined' || typeof canvas.captureStream !== 'function') {
      recorderRef.current = null;
      return;
    }
    try {
      // Lower capture fps and bitrate on mobile — phone GPUs/CPUs are
      // far more likely to drop frames or stutter at 30fps/3Mbps,
      // producing a choppy or corrupt-looking clip. 20fps/1.4Mbps is
      // still smooth enough for a short highlight clip and is much
      // more reliable on-device.
      const fps = isMobile ? 20 : 30;
      const bitrate = isMobile ? 1_400_000 : 3_000_000;
      const stream = canvas.captureStream(fps);
      // Order matters: browsers report several of these as "supported"
      // even when actual recording is unreliable, so mp4/h264 (broadly
      // supported for both recording AND playback on iOS Safari) is
      // tried before the vp9/vp8 webm variants that iOS can record but
      // cannot play back in a <video> tag.
      const mimes = isMobile
        ? [
            'video/mp4;codecs=avc1', 'video/mp4',
            'video/webm;codecs=vp8', 'video/webm',
          ]
        : [
            'video/webm;codecs=vp9', 'video/webm;codecs=vp8',
            'video/webm', 'video/mp4;codecs=avc1',
          ];
      const mime = mimes.find((m) => MediaRecorder.isTypeSupported(m)) || '';
      chunksRef.current = [];
      const rec = mime
        ? new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: bitrate })
        : new MediaRecorder(stream);
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.onerror = (e) => { console.warn('MediaRecorder error:', e); };
      rec.start(250);
      recorderRef.current = rec;
    } catch (err) {
      console.warn('MediaRecorder unavailable:', err);
      recorderRef.current = null;
    }
  }, [isMobile]);

  const stopRecording = useCallback(
    () =>
      new Promise((resolve) => {
        const rec = recorderRef.current;
        if (!rec || rec.state === 'inactive') return resolve(null);
        rec.onstop = () => {
          const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'video/webm' });
          recorderRef.current = null;
          resolve(blob);
        };
        try { rec.requestData(); rec.stop(); } catch { resolve(null); }
      }),
    []
  );

  // ── Web Audio ─────────────────────────────────────────────────────
  const ensureAudio = useCallback(() => {
    if (audioRef.current.ctx) return audioRef.current;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return null;
      const ctx    = new AC();
      const master = ctx.createGain();
      master.gain.value = 0.18;
      master.connect(ctx.destination);
      audioRef.current = { ctx, master };
      return audioRef.current;
    } catch { return null; }
  }, []);

  const beep = useCallback(
    (freq, dur = 0.08, type = 'square', vol = 1) => {
      if (muted) return;
      const a = ensureAudio();
      if (!a?.ctx || !a.master) return;
      const { ctx, master } = a;
      const osc = ctx.createOscillator();
      const g   = ctx.createGain();
      osc.type = type;
      osc.frequency.setValueAtTime(freq, ctx.currentTime);
      g.gain.setValueAtTime(0.0001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(vol,    ctx.currentTime + 0.005);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + dur);
      osc.connect(g).connect(master);
      osc.start();
      osc.stop(ctx.currentTime + dur + 0.02);
    },
    [muted, ensureAudio]
  );

  // Distinct "lost a life but still in it" sound — softer than the death explosion
  const hitSound = useCallback(() => {
    beep(320, 0.09, 'sawtooth', 0.6);
    setTimeout(() => beep(190, 0.12, 'sawtooth', 0.5), 80);
  }, [beep]);

  const explosion = useCallback(() => {
    if (muted) return;
    const a = ensureAudio();
    if (!a?.ctx || !a.master) return;
    const { ctx, master } = a;
    const size = ctx.sampleRate * 0.5;
    const buf  = ctx.createBuffer(1, size, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < size; i++)
      data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / size, 2);
    const src  = ctx.createBufferSource();
    src.buffer = buf;
    const g    = ctx.createGain();
    g.gain.value = 0.8;
    const filt = ctx.createBiquadFilter();
    filt.type  = 'lowpass';
    filt.frequency.value = 900;
    src.connect(filt).connect(g).connect(master);
    src.start();
  }, [muted, ensureAudio]);

  const winSound = useCallback(() => {
    [523, 659, 784, 1047].forEach((f, i) =>
      setTimeout(() => beep(f, 0.14, 'square', 0.9), i * 90)
    );
  }, [beep]);

  const vibrate = (pattern) => {
    try { if (navigator.vibrate) navigator.vibrate(pattern); } catch {}
  };

  const triggerShake = () => {
    setShaking(true);
    setTimeout(() => setShaking(false), 400);
  };

  // ── Game logic helpers ─────────────────────────────────────────────
  const reset = useCallback(() => {
    const s          = S.current;
    s.asteroids      = [];
    s.particles      = [];
    s.score          = 0;
    s.lives          = MAX_LIVES;
    s.lastSpawn      = 0;
    s.spawnEvery     = 460;
    s.invincibleUntil = 0;
    s.ship.x         = s.w / 2;
    setScore(0);
    setLives(MAX_LIVES);
  }, []);

  const clearCountdown = useCallback(() => {
    countdownTimers.current.forEach(clearTimeout);
    countdownTimers.current = [];
  }, []);

  const start = useCallback(() => {
    clearCountdown();
    ensureAudio();
    reset();
    S.current.status = 'countdown';
    setStatus('countdown');

    COUNTDOWN_STEPS.forEach(({ val, freq }, i) => {
      const id = setTimeout(() => {
        setCountdownVal(val);
        beep(freq, val === 'GO' ? 0.16 : 0.11, 'square', val === 'GO' ? 1 : 0.7);
        if (val === 'GO') beep(freq * 1.33, 0.14, 'square', 0.8);

        if (val === 'GO') {
          const goId = setTimeout(() => {
            S.current.status = 'playing';
            setStatus('playing');
            setCountdownVal(null);
            setTimeout(() => startRecording(), 50);
          }, COUNTDOWN_GO_HOLD_MS);
          countdownTimers.current.push(goId);
        }
      }, i * COUNTDOWN_STEP_MS);
      countdownTimers.current.push(id);
    });
  }, [reset, beep, ensureAudio, startRecording, clearCountdown]);

  // Cancel any pending countdown ticks if the component unmounts mid-countdown
  useEffect(() => clearCountdown, [clearCountdown]);

  // ── Canvas resize (reads the wrap's REAL rendered box so the canvas
  //     always exactly fills the hex screen, at any frame size) ───────
  useEffect(() => {
    const onResize = () => {
      const wrap   = wrapRef.current;
      const canvas = canvasRef.current;
      if (!wrap || !canvas) return;
      const w = wrap.clientWidth;
      const h = wrap.clientHeight;
      if (!w || !h) return;

      const dpr = window.devicePixelRatio || 1;
      canvas.width  = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width  = `${w}px`;
      canvas.style.height = `${h}px`;
      canvas.getContext('2d')?.setTransform(dpr, 0, 0, dpr, 0, 0);

      const s = S.current;
      s.w = w;
      s.h = h;
      // Scale factor so ships/asteroids/speeds feel consistent as the
      // screen gets bigger, rather than looking tiny on a large frame.
      // Floor is low (0.5) on purpose — on a narrow phone the canvas
      // itself is much smaller, so asteroids/ship need to shrink with
      // it instead of staying relatively oversized.
      s.scale    = clamp(w / 650, 0.5, 1.9);
      s.ship.w   = 34 * s.scale;
      s.ship.h   = 26 * s.scale;
      s.ship.y   = h - Math.max(34, h * 0.09);
      s.ship.x   = clamp(s.ship.x, s.ship.w / 2, w - s.ship.w / 2);

      s.starsFar  = Array.from({ length: 55 }, () => ({
        x: Math.random() * w, y: Math.random() * h,
        o: Math.random() * 0.35 + 0.12, s: Math.random() * 1 + 0.3,
      }));
      s.starsNear = Array.from({ length: 35 }, () => ({
        x: Math.random() * w, y: Math.random() * h,
        o: Math.random() * 0.5 + 0.4, s: Math.random() * 1.6 + 0.7,
      }));
    };
    onResize();
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);

  // ── Keyboard input (always preventDefault on game keys — stops the
  //     page itself from scrolling while playing) ────────────────────
  useEffect(() => {
    const down = (e) => {
      if (['ArrowLeft', 'ArrowRight', ' '].includes(e.key)) e.preventDefault();
      if (e.key === 'ArrowLeft'  || e.key === 'a') S.current.keys.left  = true;
      if (e.key === 'ArrowRight' || e.key === 'd') S.current.keys.right = true;
      if (e.key === ' ' && (S.current.status === 'idle' || S.current.status === 'lost' || S.current.status === 'won')) start();
    };
    const up = (e) => {
      if (e.key === 'ArrowLeft'  || e.key === 'a') S.current.keys.left  = false;
      if (e.key === 'ArrowRight' || e.key === 'd') S.current.keys.right = false;
    };
    window.addEventListener('keydown', down, { passive: false });
    window.addEventListener('keyup',   up);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup',   up);
    };
  }, [start]);

  // ── Pointer / touch-drag tracking (mouse hover or finger drag) ─────
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const onMove  = (e) => {
      S.current.pointerX = e.clientX - c.getBoundingClientRect().left;
    };
    const onLeave = () => { S.current.pointerX = null; };
    c.addEventListener('pointermove',  onMove);
    c.addEventListener('pointerleave', onLeave);
    return () => {
      c.removeEventListener('pointermove',  onMove);
      c.removeEventListener('pointerleave', onLeave);
    };
  }, []);

  // On-screen button handlers — same keys.left/right the keyboard uses
  const setLeftKey  = useCallback((v) => { S.current.keys.left  = v; }, []);
  const setRightKey = useCallback((v) => { S.current.keys.right = v; }, []);

  // ── Main rAF game loop ─────────────────────────────────────────────
  useEffect(() => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;

    const tick = (t) => {
      const s  = S.current;
      const dt = Math.min(40, t - (s.last || t));
      s.last   = t;

      /* ── Update ── */
      if (s.status === 'playing') {
        const moveSpeed = 0.55 * s.scale;
        if (s.pointerX != null) {
          s.ship.x += (s.pointerX - s.ship.x) * 0.22;
        } else {
          if (s.keys.left)  s.ship.x -= moveSpeed * dt;
          if (s.keys.right) s.ship.x += moveSpeed * dt;
        }
        s.ship.x = clamp(s.ship.x, s.ship.w / 2, s.w - s.ship.w / 2);

        // Asteroid spawning
        s.lastSpawn += dt;
        // Moderate ramp — noticeably busier than the original as score
        // climbs, but not the relentless late-game wall from before.
        const dynEvery = Math.max(150, s.spawnEvery - s.score * 3.6);
        if (s.lastSpawn >= dynEvery) {
          s.lastSpawn = 0;
          // Slightly bigger than the original on average, with a gentle
          // late-game size creep instead of a steep one.
          const r = (13 + Math.random() * 19 + Math.min(s.score * 0.03, 5)) * s.scale;
          const craters = Array.from({ length: 2 + Math.floor(Math.random() * 2) }, () => ({
            dx: (Math.random() - 0.5) * r * 1.1,
            dy: (Math.random() - 0.5) * r * 1.1,
            r:  r * (0.12 + Math.random() * 0.12),
          }));
          s.asteroids.push({
            x:   Math.random() * (s.w - r * 2) + r,
            y:   -r,
            r,
            vy:  (0.21 + Math.random() * 0.20 + s.score * 0.0052) * s.scale,
            // A bit more horizontal drift than the original for variety,
            // well short of the sharper zig-zags from the harder pass.
            vx:  (Math.random() - 0.5) * 0.17 * s.scale,
            rot: 0,
            vr:  (Math.random() - 0.5) * 0.012,
            craters,
          });
        }

        // Move asteroids, check collisions, score
        const ship = s.ship;
        for (let i = s.asteroids.length - 1; i >= 0; i--) {
          const a = s.asteroids[i];
          a.y   += a.vy * dt;
          a.x   += a.vx * dt;
          a.rot += a.vr * dt;

          const cx = Math.max(ship.x - ship.w / 2, Math.min(a.x, ship.x + ship.w / 2));
          const cy = Math.max(ship.y - ship.h / 2, Math.min(a.y, ship.y + ship.h / 2));
          const dx = a.x - cx;
          const dy = a.y - cy;
          const hit = dx * dx + dy * dy < a.r * a.r * 0.85;

          if (hit && t >= s.invincibleUntil) {
            s.asteroids.splice(i, 1);
            s.lives -= 1;
            setLives(s.lives);
            spawnParticles(s.particles, ship.x, ship.y, 18, {
              speed: 0.2, life: 500, colors: ['#00e5ff', '#8ff5ff', '#ffffff'],
            });
            triggerShake();

            if (s.lives <= 0) {
              s.status = 'lost';
              setStatus('lost');
              explosion();
              vibrate([100, 50, 100]);
              spawnParticles(s.particles, ship.x, ship.y, 30, {
                speed: 0.32, life: 800, colors: ['#ff3b5c', '#ffb020', '#ffffff'],
              });
              const nb = Math.max(best, s.score);
              if (nb !== best) {
                setBest(nb);
                safeStorage.set('hexabots_best', String(nb));
              }
            } else {
              hitSound();
              vibrate(80);
              s.invincibleUntil = t + INVINCIBLE_MS;
            }
            break;
          }

          if (a.y - a.r > s.h) {
            s.asteroids.splice(i, 1);
            s.score += 1;
            setScore(s.score);
            beep(1200 + Math.min(s.score * 6, 600), 0.03, 'square', 0.35);
            if (s.score >= TARGET_SCORE) {
              s.status = 'won';
              setStatus('won');
              winSound();
              vibrate(200);
              // Confetti-style burst across the upper screen to celebrate
              spawnParticles(s.particles, s.w * 0.25, s.h * 0.25, 22, {
                speed: 0.34, life: 1200, colors: ['#00e5ff', '#ffd700', '#8b5cf6', '#ffffff'], size: 4,
              });
              spawnParticles(s.particles, s.w * 0.75, s.h * 0.25, 22, {
                speed: 0.34, life: 1200, colors: ['#00e5ff', '#ffd700', '#8b5cf6', '#ffffff'], size: 4,
              });
              spawnParticles(s.particles, s.w * 0.5, s.h * 0.35, 18, {
                speed: 0.3, life: 1300, colors: ['#00e5ff', '#ffd700', '#8b5cf6'], size: 5,
              });
              stopRecording().then((blob) => onWin(blob, s.score));
              break;
            }
          }
        }
      }

      // Particles (update + prune) — kept running regardless of status.
      // Both the death explosion and the win-burst are spawned in the
      // same tick that status flips away from 'playing', so gating this
      // to 'playing' only would freeze them after a single frame instead
      // of letting them actually disperse and fade out.
      for (let i = s.particles.length - 1; i >= 0; i--) {
        const p = s.particles[i];
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.life -= dt;
        if (p.life <= 0) s.particles.splice(i, 1);
      }

      // Parallax stars — two speeds for depth. Kept running regardless of
      // status so the screen never looks "frozen" behind the idle/countdown
      // overlay — it always reads as a live device, not a paused one.
      for (const st of s.starsFar)  { st.y += 0.012 * dt; if (st.y > s.h) { st.y = 0; st.x = Math.random() * s.w; } }
      for (const st of s.starsNear) { st.y += 0.035 * dt; if (st.y > s.h) { st.y = 0; st.x = Math.random() * s.w; } }

      /* ── Draw ── */
      ctx.clearRect(0, 0, s.w, s.h);

      const bgGrad = ctx.createLinearGradient(0, 0, 0, s.h);
      bgGrad.addColorStop(0, '#03060d');
      bgGrad.addColorStop(1, '#070b16');
      ctx.fillStyle = bgGrad;
      ctx.fillRect(0, 0, s.w, s.h);

      ctx.fillStyle = '#2a3550';
      for (const st of s.starsFar) { ctx.globalAlpha = st.o; ctx.fillRect(st.x, st.y, st.s, st.s); }
      ctx.fillStyle = '#00e5ff';
      for (const st of s.starsNear) { ctx.globalAlpha = st.o; ctx.fillRect(st.x, st.y, st.s, st.s); }
      ctx.globalAlpha = 1;

      ctx.strokeStyle = 'rgba(0,229,255,0.08)';
      ctx.lineWidth   = 1;
      for (let y = 0; y < s.h; y += 24) {
        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(s.w, y); ctx.stroke();
      }

      // Asteroids — rocky gradient body + fixed craters
      for (const a of s.asteroids) {
        ctx.save();
        ctx.translate(a.x, a.y);
        ctx.rotate(a.rot);
        ctx.beginPath();
        for (let i = 0; i < 7; i++) {
          const ang = (i / 7) * Math.PI * 2;
          const rr  = a.r * (0.78 + ((i * 31) % 7) / 20);
          const px  = Math.cos(ang) * rr;
          const py  = Math.sin(ang) * rr;
          i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py);
        }
        ctx.closePath();
        const rockGrad = ctx.createRadialGradient(-a.r * 0.2, -a.r * 0.2, a.r * 0.1, 0, 0, a.r);
        rockGrad.addColorStop(0, '#4a4258');
        rockGrad.addColorStop(1, '#14101c');
        ctx.fillStyle   = rockGrad;
        ctx.strokeStyle = '#ffb020';
        ctx.lineWidth   = 2;
        ctx.shadowColor = '#ffb020';
        ctx.shadowBlur  = 10;
        ctx.fill();
        ctx.stroke();
        ctx.shadowBlur = 0;
        ctx.fillStyle  = 'rgba(0,0,0,0.35)';
        for (const c of a.craters) {
          ctx.beginPath();
          ctx.arc(c.dx, c.dy, c.r, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      }

      // Particles
      for (const p of s.particles) {
        const alpha = Math.max(0, p.life / p.maxLife);
        ctx.globalAlpha = alpha;
        ctx.fillStyle   = p.color;
        ctx.shadowColor = p.color;
        ctx.shadowBlur  = 6;
        ctx.fillRect(p.x - p.size / 2, p.y - p.size / 2, p.size, p.size);
      }
      ctx.globalAlpha = 1;
      ctx.shadowBlur  = 0;

      // Ship — gradient hull + wing highlight + flickering engine, blinks while invincible
      const blinking    = t < s.invincibleUntil;
      const shipVisible = !blinking || Math.floor(t / 100) % 2 === 0;
      if (shipVisible) {
        const ship = s.ship;
        ctx.save();
        ctx.translate(ship.x, ship.y);

        const hullGrad = ctx.createLinearGradient(0, -ship.h / 2, 0, ship.h / 2);
        hullGrad.addColorStop(0,    '#d8f9ff');
        hullGrad.addColorStop(0.45, '#00e5ff');
        hullGrad.addColorStop(1,    '#3d2a6e');
        ctx.shadowColor = '#00e5ff';
        ctx.shadowBlur  = 20;
        ctx.fillStyle   = hullGrad;
        ctx.beginPath();
        ctx.moveTo(0, -ship.h / 2);
        ctx.lineTo(ship.w / 2,  ship.h / 2);
        ctx.lineTo(0,  ship.h / 4);
        ctx.lineTo(-ship.w / 2, ship.h / 2);
        ctx.closePath();
        ctx.fill();

        ctx.shadowBlur  = 0;
        ctx.strokeStyle = 'rgba(255,255,255,0.35)';
        ctx.lineWidth   = 1;
        ctx.beginPath();
        ctx.moveTo(0, -ship.h / 2);
        ctx.lineTo(ship.w / 2, ship.h / 2);
        ctx.stroke();

        ctx.fillStyle = '#0a0f1c';
        ctx.beginPath();
        ctx.arc(0, -2, 4 * (ship.w / 34), 0, Math.PI * 2);
        ctx.fill();

        if (s.status === 'playing' || s.status === 'countdown') {
          const warmingUp  = s.status === 'countdown';
          const flameCount = warmingUp ? 2 : 3;
          for (let i = 0; i < flameCount; i++) {
            const fx   = (Math.random() - 0.5) * 10 * (ship.w / 34);
            const flen = (warmingUp ? (3 + Math.random() * 5) : (6 + Math.random() * 10)) * (ship.w / 34);
            ctx.fillStyle = i === 0 ? 'rgba(220,245,255,0.9)' : 'rgba(139,92,246,0.55)';
            ctx.beginPath();
            ctx.moveTo(fx - 3, ship.h / 2);
            ctx.lineTo(fx + 3, ship.h / 2);
            ctx.lineTo(fx, ship.h / 2 + flen);
            ctx.closePath();
            ctx.fill();
          }
        }
        ctx.restore();
      }

      s.raf = requestAnimationFrame(tick);
    };

    const s = S.current;
    s.raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(s.raf);
  }, [best, onWin, beep, hitSound, explosion, winSound, stopRecording]);

  // ── Render ─────────────────────────────────────────────────────────
  return (
    <div className="w-full select-none">

      {/* ═══ HUD — a row of chips sitting above the cockpit ═══ */}
      <div className="flex items-center justify-between gap-2 mb-3 px-1 flex-wrap">
        <div className="hud-chip">
          <span className="text-dim font-mono-hud text-xs">SCORE</span>
          <span key={score} className="value-pop-anim">
            <GlowText text={`${String(score).padStart(3, '0')}/${TARGET_SCORE}`} size={16} variant="cyan" mono />
          </span>
        </div>

        <div className="flex items-center gap-1.5">
          {Array.from({ length: MAX_LIVES }).map((_, i) => (
            <LifeIcon key={i} filled={i < lives} />
          ))}
        </div>

        <div className="flex items-center gap-2">
          <div className="hud-chip">
            <span className="text-dim font-mono-hud text-xs">BEST</span>
            <GlowText text={String(best).padStart(3, '0')} size={16} variant="violet" mono />
          </div>
          <button
            type="button"
            onClick={() => setMuted((m) => !m)}
            aria-label={muted ? 'Unmute' : 'Mute'}
            className="btn-robotic btn-robotic-ghost w-9 h-9 !px-0 !py-0 text-sm"
          >
            {muted ? '🔇' : '🔊'}
          </button>
        </div>
      </div>

      {/* Progress gauge toward the target score */}
      <div
        className="w-full h-2.5 overflow-hidden mb-4"
        style={{
          background: 'rgba(0,229,255,0.08)',
          border: '1px solid var(--steel)',
          clipPath: 'polygon(6px 0, 100% 0, 100% calc(100% - 6px), calc(100% - 6px) 100%, 0 100%, 0 6px)',
        }}
      >
        <div
          className={score >= TARGET_SCORE * 0.85 ? 'progress-near-win' : undefined}
          style={{
            position:   'relative',
            overflow:   'hidden',
            width:      `${clamp((score / TARGET_SCORE) * 100, 0, 100)}%`,
            height:     '100%',
            background: 'linear-gradient(90deg, var(--violet), var(--cyan))',
            boxShadow:  '0 0 8px var(--cyan)',
            transition: 'width 0.3s ease-out',
          }}
        >
          <div className="progress-shimmer" />
        </div>
      </div>

      {/* ═══ Cockpit bezel + game screen ═══ */}
      <div
        className={`hexabots-frame${shaking ? ' mech-hit-shake' : ''}`}
        style={{ position: 'relative' }}
      >
        <div
          className="cockpit-bezel"
          style={{ aspectRatio: FRAME_ASPECT, position: 'relative' }}
        >
          {/* Corner rivets */}
          <span className="cockpit-rivet" style={{ top: 8, left: 8 }} />
          <span className="cockpit-rivet" style={{ top: 8, right: 8 }} />
          <span className="cockpit-rivet" style={{ bottom: 'clamp(18px, 5vw, 34px)', left: 8 }} />
          <span className="cockpit-rivet" style={{ bottom: 'clamp(18px, 5vw, 34px)', right: 8 }} />

          {/* Screen viewport — fills the bezel's padded content box */}
          <div
            ref={wrapRef}
            className="cockpit-screen"
            style={{ width: '100%', height: '100%' }}
          >
            <canvas
              ref={canvasRef}
              className="block w-full h-full"
              style={{ background: '#020408', touchAction: 'none' }}
            />

            {/* Damage flash — same trigger/timing as the frame shake */}
            <div
              className={shaking ? 'hit-flash-active' : undefined}
              style={{
                position: 'absolute', inset: 0,
                background: 'radial-gradient(circle, rgba(255,59,92,0.55), transparent 70%)',
                pointerEvents: 'none',
                opacity: 0,
                zIndex: 18,
              }}
            />

            {/* Idle / countdown / lost / won overlay */}
            {status !== 'playing' && (
              <div
                className="absolute inset-0 flex items-center justify-center"
                style={{ background: 'rgba(3,6,13,0.82)', backdropFilter: 'blur(2px)', zIndex: 20 }}
              >
                <div key={status} className="text-center px-4 overlay-pop-in" style={{ maxWidth: '86%' }}>

                  {status === 'idle' && (
                    <>
                      <GlowText
                        as="div" text="SECURE WL" variant="cyan"
                        className="mb-3" style={{ fontSize: 'clamp(14px, 3vw, 22px)' }}
                      />
                      <p className="font-mono-hud mb-3" style={{ fontSize: 'clamp(12px, 2.2vw, 17px)', color: 'var(--text)' }}>
                        Pilot the HEXABOT. Dodge every stone. You've got{' '}
                        <span style={{ color: 'var(--cyan)', fontWeight: 'bold' }}>{MAX_LIVES}</span>{' '}
                        lives — reach{' '}
                        <span style={{ color: 'var(--cyan)', fontWeight: 'bold' }}>{TARGET_SCORE}</span>{' '}
                        to unlock wallet submission.
                      </p>
                      <p className="font-mono-hud mb-4 text-dim" style={{ fontSize: 'clamp(10px, 1.8vw, 14px)' }}>
                        {isMobile ? 'Move: on-screen arrows / drag' : 'Move: ← → · A D · mouse'}
                      </p>
                      <RoboticButton onClick={start} variant="cyan" className="cta-button">
                        PLAY GAME
                      </RoboticButton>
                    </>
                  )}

                  {status === 'countdown' && (
                    <div
                      key={String(countdownVal)}
                      className="glow-text glow-cyan countdown-num"
                      style={{
                        fontSize: countdownVal === 'GO' ? 'clamp(34px, 9vw, 60px)' : 'clamp(46px, 12vw, 84px)',
                      }}
                    >
                      {countdownVal === 'GO' ? 'GO!' : countdownVal}
                    </div>
                  )}

                  {status === 'lost' && (
                    <>
                      <GlowText
                        as="div" text="SHIP DESTROYED" variant="danger"
                        className="mb-3" style={{ fontSize: 'clamp(14px, 3vw, 22px)' }}
                      />
                      <p className="font-mono-hud mb-4" style={{ fontSize: 'clamp(12px, 2.2vw, 17px)', color: 'var(--text)' }}>
                        Score: {score} / {TARGET_SCORE}
                      </p>
                      <RoboticButton onClick={start} variant="cyan" className="cta-button">
                        RETRY RUN
                      </RoboticButton>
                    </>
                  )}

                  {status === 'won' && (
                    <>
                      <GlowText
                        as="div" text="RUN COMPLETE" variant="cyan"
                        className="mb-3 win-title-glow" style={{ fontSize: 'clamp(14px, 3vw, 22px)' }}
                      />
                      <p className="font-mono-hud" style={{ fontSize: 'clamp(12px, 2.2vw, 17px)', color: 'var(--text)' }}>
                        WL access granted. Submit wallet below to secure it.
                      </p>
                    </>
                  )}

                </div>
              </div>
            )}
          </div>

          {/* On-screen touch controls — mounted on the bezel's control ledge.
              Shown from the countdown onward so thumbs are already in position
              the instant "GO" hits. */}
          {isMobile && (status === 'playing' || status === 'countdown') && (
            <>
              <TouchButton side="left"  onDown={() => setLeftKey(true)}  onUp={() => setLeftKey(false)} />
              <TouchButton side="right" onDown={() => setRightKey(true)} onUp={() => setRightKey(false)} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
